// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API_ENDPOINT_SYMBOLS, API_ENDPOINT_LOGOUT, API_ENDPOINT_USER_LEVERAGE, API_ENDPOINT_DIRECT_LOGIN, API_ENDPOINT_GENERATE_CHILD_LOGIN_TOKEN } from "../data/Endpoints-API";
import { WS_ENDPOINT_PLATFORM_LIVE_FEEDS } from "../data/Endpoints-WS";
import APIMiddleware from "../data/api/Api-Middleware";
import { resolveClientPublicIp, appendClientReportedIpToUrl } from "../utils/manualActionAudit";
import { getOrCreateSessionVariantId, persistSessionVariantId } from "../utils/sessionVariantId";
import { clearChallengeSwitchContext, getChallengeSwitchContext } from "../utils/challengeSwitchContext";
import { ws_close_all, ws_create, ws_is_open } from "../data/websocket/Websocket-Middleware";
import { createChartSubscriptionTracker, matchesChartSubscription, clearStaleChartPacket, listenToPlatformEvents } from "../data/websocket/chartSubscription";
import { watchPlatformRecovery } from '../data/websocket/platformRecovery';
import { webSocketManagerGlobal } from '../data/websocket/Websocket-Manager';
import { DIRECT_LOGIN_PROGRESS_KEY, hasDirectLoginTokenInUrl } from "../utils/directLoginBootstrap";
import {
  clearSessionTokens,
  persistSessionTokens,
  startSessionRefreshScheduler,
  stopSessionRefreshScheduler,
  REFRESH_TOKEN_KEY,
  SESSION_TOKEN_REFRESHED_EVENT,
} from "../utils/sessionRefresh";

// import { IdleTimerProvider, useIdleTimer } from 'react-idle-timer';
import * as PusherPushNotifications from "@pusher/push-notifications-web";

export const AuthContext = createContext();

export function useAuthContext() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [connectionClosed, setConnectionClosed] = useState(false);

  const platformContextRef = useRef({});
  const connectionAttemptRef = useRef(null);
  const connectionStateRef = useRef({ connected: false, url: null });
  const subscriptionTrackerRef = useRef(null);
  if (!subscriptionTrackerRef.current) subscriptionTrackerRef.current = createChartSubscriptionTracker();

  useEffect(() => {
    let hasOpened = false;
    const dispose = listenToPlatformEvents(document, () => ({
      opened: event => {
        connectionStateRef.current = {
          connected: true,
          url: connectionAttemptRef.current?.url || connectionStateRef.current.url,
        };
        setIsWebSocketConnected(true);
        platformContextRef.current.sendDataToServer();
        // Symbol quotes are subscribed on mount, which can be before this socket
        // exists. Flip the flag so the list sends SymbolsPagination again.
        setIsWebSocketRefetch((value) => !value);
        if (event?.detail) setConnectionClosed(false);
        if (hasOpened) setIsReconnected(true);
        hasOpened = true;
      },
      closed: event => {
        connectionStateRef.current.connected = false;
        setIsWebSocketConnected(false);
        if (event?.detail) setConnectionClosed(true);
      },
      message: event => platformContextRef.current.handleLiveFeedData(event),
    }));
    const stopRecovery = watchPlatformRecovery({
      win: window, doc: document,
      enabled: () => !!platformContextRef.current.user?.userId && !platformContextRef.current.isLoggingOut,
      resubscribe: () => platformContextRef.current.sendDataToServer(),
      restart: () => webSocketManagerGlobal.restartCurrent(),
    });
    return () => { dispose(); stopRecovery(); };
  }, []);

  const [user, setUser] = useState({
    userId: 0,
    userName: "",
    total_deposit: 0,
    total_withdrawal: 0,
    total_margin: 0,
    total_realized_pnl: 0,
    totalUnrealizedGrossPnL: 0,
    tempData: "",
    isAuthorized: false,
    is_bot_subscribed: false,
    is_bot_enabled: false,
    bot_paused_by_admin: false,
  });
  
  const [fontSize, setFontSize] = useState(12);
  const [reloadApi, setReloadApi] = useState("");
  const [font, setFont] = useState(useState(() => localStorage.getItem('storedFont') || 'Large'));
  const [fontSizeTrigger, setFontSizeTrigger] = useState('');
  const [fontUpdating, setFontUpdating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [selectedAuthSymbol, setAuthSelectedSymbol] = useState('');
  const [selectedAuthSymbolId, setSelectedAuthSymbolId] = useState('');
  const [, setDefaultSelectedSymbol] = useState('');
  const [defaultSelectedCategory, setDefaultSelectedCategory] = useState('');
  const [selectedAuthTimeFrame, setAuthTimeFrame] = useState(() => localStorage.getItem('timeFrame') || '15m');
  const [authSelectedCategory, setAuthSelectedCategory] = useState(1);
  const [allValidSymbols, setAllValidSymbols] = useState([]);
  const [isMariginCallVisible, setIsMariginCallVisible] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false)
  const [isWebSocketRefetch, setIsWebSocketRefetch] = useState(false)
  const [resetPasswordModal, showResetPasswordModal] = useState(false);
  const [isFirstLoginDisabled, setIsFirstLoginDisabled] = useState(false);
  const [showFloatingWindow, setShowFloatingWindow] = useState(() => {
    const storedValue = localStorage.getItem('showFloatingWindow');
    return storedValue === null ? true : JSON.parse(storedValue);
  });
  const [variantLogout , setVariantLogout] = useState(false);
  //create web-socket instance for the component
  const ws_platform = useMemo(() => ws_create(), []);
  const WS_MESSAGE_EVENT_LISTENER = "ws_message_platform";
  // const data = [];

  const [platFromData, setPlatFromData] = useState([[],[], [], [], [], [], [], [], []]); // Initial data state
  const [leverageData, setLeverageData] = useState(null); // Store leverage data fetched from API
  const [isReconnected, setIsReconnected] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [loginPortalModal, setLoginPortalModal] = useState(false);
  const chartSubscription = subscriptionTrackerRef.current.select({
    userId: user?.userId, symbol: selectedAuthSymbol,
    timeFrame: selectedAuthTimeFrame, category: authSelectedCategory,
  });
  const currentPlatformData = useMemo(
    () => clearStaleChartPacket(platFromData, chartSubscription),
    [platFromData, chartSubscription],
  );
  // Clear retained ticks as well as masking them synchronously for child effects.
  useEffect(() => {
    setPlatFromData(previous => clearStaleChartPacket(previous, chartSubscription));
  }, [chartSubscription]);
  const symbolCatalogRequestRef = useRef(0);
  const leverageRequestRef = useRef(0);

  const updateData = (index, newData, keepPrevData = false) => {
   
    setPlatFromData((prevData) => {
      const updatedData = [...prevData];
      if (keepPrevData) updatedData[index] = {...updatedData[index], keepPrevData};
      else updatedData[index] = newData;
      return updatedData;
    });
  };

  const incrementSystemNotificationCount = () => {
    setPlatFromData((prevData) => {
      const updatedData = [...prevData];
      const metrics = { ...(updatedData[5] || {}) };
      metrics.userUnseenSystemNotificationsCount =
        (metrics.userUnseenSystemNotificationsCount || 0) + 1;
      updatedData[5] = metrics;
      return updatedData;
    });
  };

  const setSystemNotificationCount = (count) => {
    setPlatFromData((prevData) => {
      const updatedData = [...prevData];
      const metrics = { ...(updatedData[5] || {}) };
      metrics.userUnseenSystemNotificationsCount = Number(count) || 0;
      updatedData[5] = metrics;
      return updatedData;
    });
  };

  const setAlertNotificationCount = (count) => {
    setPlatFromData((prevData) => {
      const updatedData = [...prevData];
      const metrics = { ...(updatedData[5] || {}) };
      metrics.userUnseenNotificationsCount = Number(count) || 0;
      updatedData[5] = metrics;
      return updatedData;
    });
  };

  useEffect(() => {
    localStorage.setItem('showFloatingWindow', JSON.stringify(showFloatingWindow));
  }, [showFloatingWindow]);

  const logoutRef = useRef(null);

  const forceLocalLogout = () => {
    stopSessionRefreshScheduler();
    clearChallengeSwitchContext();
    clearSessionTokens();
    localStorage.removeItem('userId');
    localStorage.removeItem('is_margin_call_visible');
    if (localStorage.variantId != undefined) {
      localStorage.removeItem('variantId');
    }
    ws_close_all().finally(() => {
      window.location.reload();
    });
  };

  // Resume silent refresh after page reload if session tokens exist.
  // Do NOT tear down an open platform socket on access-token refresh (that blanks prices).
  // If the socket is already down, open it with the new access token.
  useEffect(() => {
    let cancelled = false;
    const onSessionExpired = () => {
      if (
        hasDirectLoginTokenInUrl() ||
        sessionStorage.getItem(DIRECT_LOGIN_PROGRESS_KEY) === '1'
      ) {
        return;
      }
      forceLocalLogout();
    };
    const onTokenRefreshed = () => {
      if (ws_is_open()) return;
      const userId = user?.userId || localStorage.userId;
      if (localStorage.variantId && localStorage.token && userId) {
        ws_platform.ws_connect(
          WS_ENDPOINT_PLATFORM_LIVE_FEEDS(userId, 10, '100ms', localStorage.variantId, localStorage.token),
          WS_MESSAGE_EVENT_LISTENER
        ).catch((e) => console.warn('WS connect after refresh failed', e));
      }
    };
    window.addEventListener('sessionExpired', onSessionExpired);
    window.addEventListener(SESSION_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    (async () => {
      // Login-as-user (?token=) must not refresh or revive the previous session.
      if (hasDirectLoginTokenInUrl() || sessionStorage.getItem(DIRECT_LOGIN_PROGRESS_KEY) === '1') {
        return;
      }
      if (localStorage.getItem(REFRESH_TOKEN_KEY) && localStorage.getItem('token')) {
        await startSessionRefreshScheduler();
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
      stopSessionRefreshScheduler();
      window.removeEventListener('sessionExpired', onSessionExpired);
      window.removeEventListener(SESSION_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    };
  }, [user?.userId]);
  
  // useEffect(()=>{
  //   setFontSize(2);
  //   setFontSizeTrigger('2LD');
  // },[])



  // const handleOnIdle = async () => {
  //   if (user && user.userId != undefined && user.userId > 0) {
  //     ws_close_all();
  //     setShowLogoutModal(true)
  //   }
  // };

  // const handleOnActive = () => {
  //   // Optional: Handle what happens when the user becomes active again
  //   // console.log('User is active');
  // };

  // const handleOnAction = () => {
  //   // Optional: Handle what happens on any user action
  //  // console.log('User action detected');
  // };

  // Set idle timeout to 30 minutes
  // const { reset } = useIdleTimer({
  //   timeout: 30 * 60 * 1000,
  //   onIdle: handleOnIdle,
  //   onActive: handleOnActive,
  //   onAction: handleOnAction
  // });



  const fontAdjuster = (e) => {
    setFontUpdating(true);
    const selectedFont = e.target.value;
    if (selectedFont === 'Large') {
      if (font === 'Default') {
        console.log('def: large')
        setFontSize(2);
        setFontSizeTrigger('2LD');
      } else if (font === 'Small') {
        console.log('small: large')
        setFontSize(4);
        setFontSizeTrigger('4LS');
      }
    } else if (selectedFont === 'Default') {
      if (font === 'Large') {
        setFontSize(-2);
        setFontSizeTrigger('-2DL');
      } else if (font === 'Small') {
        setFontSize(2);
        setFontSizeTrigger('-2DS');
      }
    } else if (selectedFont === 'Small') {
      if (font === 'Default') {
        setFontSize(-2);
        setFontSizeTrigger('-2SD');
      } else if (font === 'Large') {
        setFontSize(-4);
        setFontSizeTrigger('-2SL');
      }      
    }
    setFont(selectedFont);
  };


 
  useEffect(() => {
    if (chartSubscription) platformContextRef.current.loadPlatform(platformContextRef.current.user);
  }, [chartSubscription, allValidSymbols]);

  useEffect(() => {

    if (user && user.userId != undefined && user.userId > 0) {
      getAllValidSymbols();

    }
  }, [user?.userId]);


  useEffect(() => {
    
    if (user && user.userId != undefined && user.userId > 0) {
      // console.log(user, user.userId);

    registerDeviceWithBeams(user.userId);
    
    // beamsClient.start()
    //   .then((beamsClient) => beamsClient.getDeviceId())
    //   .then((deviceId) =>
    //     console.log("Successfully registered with Beams. Device ID:", deviceId)
    //   )
    //   .then(() => beamsClient.addDeviceInterest("user-push-notification-subscriber-id" + user.userId))
    //   .then(() => beamsClient.getDeviceInterests())
    //   .then((interests) => console.log("Current interests:", interests))
    //   .catch((error) => console.error(error));
    }
  }, [user?.userId]);


  useEffect(() => {
    
    if (user && user.userId != undefined && user.userId > 0) {
      if(user?.isFirstLogin && user?.isQuickRegistered){
        showResetPasswordModal(true);
        setIsFirstLoginDisabled(true)
      }
    }
  }, [user?.userId]);


  const registerDeviceWithBeams = async (userId) => {
    try {
      const beamsClient = new PusherPushNotifications.Client({
        instanceId: process.env.REACT_APP_INSTANCE_KEY,
      });

        // Start Beams client
      await beamsClient.start();

      // await beamsClient.removeDeviceInterest('user-push-notification-subscriber-id85')
      await beamsClient.clearAllState()

      // Clear any previous interests
      // await beamsClient.clearDeviceInterests();
      // console.log("Cleared previous device interests");

      // Get Device ID
      const deviceId = await beamsClient.getDeviceId();
      // console.log("Successfully registered with Beams. Device ID:", deviceId);

      // Add interest for the current user
      await beamsClient.addDeviceInterest("user-push-notification-subscriber-id" + userId);
      // console.log(`Added interest: user-push-notification-subscriber-id${userId}`);

      // Get current interests
      const interests = await beamsClient.getDeviceInterests();
      // console.log("Current interests:", interests);
    } catch (error) {
      // console.error("Error with Beams registration:", error);
    }
  }
  
  const getAllValidSymbols = async () => {
    const accountId = user?.userId;
    const request = ++symbolCatalogRequestRef.current;
    const response = await APIMiddleware.get(API_ENDPOINT_SYMBOLS(accountId));
    if (request !== symbolCatalogRequestRef.current || String(platformContextRef.current.user?.userId) !== String(accountId)) return;
    if (response.data?.[0]?.length) {

      setAllValidSymbols(response.data);
      setDefaultSelectedSymbol(response.data[0][0].name)
      setDefaultSelectedCategory(response.data[0][0].symbol_category)
    }
  }

  /**
   * Fetch user leverage data from API for selected symbol only
   * @param {number} userId - User ID
   * @param {string} symbolName - Symbol name (required)
   */
  const fetchUserLeverage = useCallback(async (userId, symbolName) => {
    if (!userId || !symbolName) {
      return;
    }
    
    const request = ++leverageRequestRef.current;
    const isCurrent = () => request === leverageRequestRef.current
      && String(platformContextRef.current.user?.userId) === String(userId)
      && platformContextRef.current.chartSubscription?.symbol === symbolName;
    try {
      const endpoint = API_ENDPOINT_USER_LEVERAGE(userId, symbolName);
      const response = await APIMiddleware.get(endpoint);
      if (!isCurrent()) return;
      // APIMiddleware.get() returns response.data directly (not wrapped in { data: ... })
      // So response IS the data: { success: true, leverage: {...} }
      const responseData = response;
      
      if (responseData && responseData.success === true && responseData.leverage) {
        // Store only the selected symbol's leverage (single object, not array)
        const leverage = responseData.leverage;
        setLeverageData(leverage);
      } else {
        setLeverageData(null);
      }
    } catch (error) {
      console.error('Error fetching user leverage:', error);
      if (!isCurrent()) return;
      setLeverageData(null); // Clear on error
    }
  }, []);

  // Refetch leverage when WebSocket reconnects and leverage data is missing
  useEffect(() => {
    if (isWebSocketConnected && user?.userId && selectedAuthSymbol && !leverageData) {
      fetchUserLeverage(user.userId, selectedAuthSymbol);
    }
  }, [isWebSocketConnected, user?.userId, selectedAuthSymbol, leverageData, fetchUserLeverage]);

  const login = (userObj) => {
    // Implement login logic, e.g., by making an API request to server.
    // If login is successful, set the user.
    setUser(userObj);
    localStorage.setItem('accountManager', 'open-positions-acc');
    // loadPlatform(userObj);
  };

  const updateUserAuthorization = () => {
    user.isAuthorized = !user.isAuthorized;
  };

  const updateUserData = (updatedUserAccountData) => {
    if (user && user.userId != undefined && user.userId > 0) {
      // If the user is logged in, update account data
      setUser(updatedUserAccountData);
    }
  };

  const updateUserData_Local = (value) => {
    setUser((prevUserData) => ({
      ...prevUserData,
      tempData: value,
    }));
  };



  // One listener owns incoming packets. Connection attempts only establish transport.
  const loadPlatform = async (account = user) => {
    const current = platformContextRef.current;
    const subscription = current.chartSubscription;
    if (!subscription || String(account?.userId) !== subscription.userId
      || !localStorage.variantId || !localStorage.token) return;
    const url = WS_ENDPOINT_PLATFORM_LIVE_FEEDS(subscription.userId, 10, '100ms', localStorage.variantId, localStorage.token);
    const feedKey = (value) => String(value || '').replace(/([?&])token=[^&]*/g, '$1');
    if (connectionStateRef.current.connected && feedKey(connectionStateRef.current.url) === feedKey(url)) {
      current.sendDataToServer();
      return;
    }
    const pending = connectionAttemptRef.current;
    if (pending) {
      if (pending.url === url) return pending.promise;
      // Account changes wait for the previous attempt before replacing its socket.
      return pending.promise.then(() => platformContextRef.current.loadPlatform(platformContextRef.current.user));
    }
    const attempt = { url, promise: null };
    connectionAttemptRef.current = attempt;
    attempt.promise = ws_platform.ws_connect(url, WS_MESSAGE_EVENT_LISTENER)
      .then(() => {
        const latest = platformContextRef.current;
        if (latest.chartSubscription?.userId === subscription.userId) {
          latest.fetchUserLeverage(account.userId, latest.chartSubscription.symbol);
        }
      })
      .finally(() => {
        if (connectionAttemptRef.current === attempt) connectionAttemptRef.current = null;
      });
    return attempt.promise;
  };

  const handleLiveFeedData = (event) => {
    const packet = event?.detail;
    if (!packet || !user?.userId) return;
    const packetUser = packet.user ?? packet.userId;
    if (packetUser != null && String(packetUser) !== String(user.userId)) return;
    if ((packet.type === 'klines' || packet.type === 'selectedSymbol')
      && !matchesChartSubscription(packet, chartSubscription)) return;
    // Update data based on event detail
    if (event.detail.type === 'symbols') {
      updateData(0, event.detail.updatedSymbolData, false);
    } else if (event.detail.type === 'klines') {
      updateData(1, event.detail, false);
    } else if (event.detail.type === 'depth') {
      updateData(2, event.detail, false);
    } else if (event.detail.type === 'positions') {
      updateData(3, event.detail, event.detail.keepPrevData);
    } else if (event.detail.type === 'orders') {
      updateData(4, event.detail, event.detail.keepPrevData);
    } else if (event.detail.type === 'user_details') {
      // Account currency can change while the session is open. Keep the
      // symbol conversion inputs aligned with authoritative WS metrics.
      if (event.detail.userCurrencyName) {
        setUser(previous => {
          if (!previous) return previous;
          const currency = String(event.detail.userCurrencyName).trim().toUpperCase();
          const symbol = event.detail.userCurrencySymbol ?? previous.userCurrencySymbol;
          if (previous.userCurrencyName === currency && previous.userCurrencySymbol === symbol) return previous;
          return { ...previous, userCurrencyName: currency, userCurrencySymbol: symbol };
        });
      }
      if (event.detail?.logoutVariants?.length && event.detail.logoutVariants.includes(localStorage.variantId)) {
        setVariantLogout(true);
        setTimeout(() => {
          logout();
          setVariantLogout(false);
        }, 3000);
      }
      updateData(5, event.detail, event.detail.keepPrevData);
    } else if (event.detail.type === "leverageUpdated") {
      // Fetch updated leverage via API when flag is received
      const { userId, symbolName, timestamp } = event.detail;
      const symbolToFetch = symbolName || selectedAuthSymbol;

      console.log('[LEV][WEB] leverageUpdated DOM event received in Auth-Context:', {
        eventUserId: userId,
        ctxUserId: user?.userId,
        symbolName,
        selectedAuthSymbol,
        symbolToFetch,
        timestamp,
        socketUrl: process.env.REACT_APP_SOCKET_URL,
      });

      // Always refetch if we have userId and a symbol
      if (user?.userId && symbolToFetch) {
        console.log('[LEV][WEB] Auth-Context -> calling fetchUserLeverage after leverageUpdated:', {
          userId: user.userId,
          symbolToFetch,
          socketUrl: process.env.REACT_APP_SOCKET_URL,
        });
        fetchUserLeverage(user.userId, symbolToFetch);
      } else {
        console.warn('[LEV][WEB] leverageUpdated event received but missing userId or symbolToFetch', {
          hasUser: !!user?.userId,
          symbolToFetch,
        });
      }
    } else if (event.detail.type === 'selectedSymbol') {
      // Only clear leverage data if symbol actually changed
      const newSymbol = event.detail.selectedSymbolName || event.detail.symbolName;
      if (newSymbol && newSymbol !== selectedAuthSymbol) {
        // Symbol changed - clear and refetch for new symbol
        setLeverageData(null);
        if (user?.userId && newSymbol) {
          fetchUserLeverage(user.userId, newSymbol);
        }
      } else if (newSymbol && newSymbol === selectedAuthSymbol && !leverageData) {
        // Same symbol but leverage data is missing - refetch it
        if (user?.userId) {
          fetchUserLeverage(user.userId, newSymbol);
        }
      }
      updateData(8, event.detail, false);
    } else if (event.detail.type === "Bot") {
      updateData(7, event.detail, false);
    } else if (event.detail.type === 'sendDataAgain') {
      sendDataToServer();
      setIsWebSocketRefetch(true);
    }
  };

  const sendDataToServer = (data) => {
    const subscription = platformContextRef.current.chartSubscription;
    if (!subscription || !localStorage.variantId || !localStorage.token) return;
    const url = WS_ENDPOINT_PLATFORM_LIVE_FEEDS(subscription.userId, 10, '100ms', localStorage.variantId, localStorage.token);
    if (data) ws_platform.ws_send(url, JSON.stringify(data));
    ws_platform.ws_send(url, JSON.stringify({
      type: 'selectedSymbolChange',
      selectedSymbolName: subscription.symbol,
      selectedSymbolTimeFrame: subscription.timeFrame,
      selectedCategory: subscription.category,
      requestId: subscription.requestId,
    }));
  };


  const logout = async () => {
    setIsLoggingOut(true);
    const userId = localStorage.userId;
    const variantId = localStorage.variantId;
    // Implement logout logic, e.g., by clearing user data or revoking tokens.
    try {
      await resolveClientPublicIp();
      const logoutUrl = await appendClientReportedIpToUrl(
        API_ENDPOINT_LOGOUT(variantId, userId)
      );
      console.log(logoutUrl, "APIMiddleware");
      const response = await APIMiddleware.get(logoutUrl, {
        'X-Refresh-Token': localStorage.getItem('refreshToken') || '',
      });
      console.log(response, "response from logout");
    } catch (logoutError) {
      console.log('Logout error:', logoutError);
    }
    await ws_close_all();
    clearChallengeSwitchContext();
    stopSessionRefreshScheduler();
    clearSessionTokens();
    localStorage.removeItem("userId");
    localStorage.removeItem('is_margin_call_visible');

    if (localStorage.variantId != undefined) {
      localStorage.removeItem("variantId");
    }
    setUser(null);
    // document.body.className = "";
    window.location.reload();
    return null;
  };
  logoutRef.current = logout;
  const logoutWithOutRefresh = async () => {
    const userId = localStorage.userId;
    const variantId = localStorage.variantId;
    try {
      await resolveClientPublicIp();
      const logoutUrl = await appendClientReportedIpToUrl(
        API_ENDPOINT_LOGOUT(variantId, userId)
      );
      await APIMiddleware.get(logoutUrl, {
        'X-Refresh-Token': localStorage.getItem('refreshToken') || '',
      });
    } catch (logoutError) {
      console.log('logoutWithOutRefresh error:', logoutError);
    }
    await ws_close_all();
    stopSessionRefreshScheduler();
    clearSessionTokens();
    localStorage.removeItem("userId");
    localStorage.removeItem('is_margin_call_visible');

    if (localStorage.variantId != undefined) {
      localStorage.removeItem("variantId");
    }
    setUser(null);
    // document.body.className = "";
    // setTimeout(() => {
    //   window.location.reload();
    // }, 300);
  };

  const loginWithDirectToken = async (token) => {
    if (!token) {
      return false;
    }

    try {
      // Stop the current refresh before it can overwrite the account we are switching to.
      stopSessionRefreshScheduler();
      if (localStorage.variantId) {
        try {
          await logoutWithOutRefresh();
        } catch (error) {
          console.warn('Previous session logout failed, continuing direct login', error);
        }
      }

      const response = await APIMiddleware.post(API_ENDPOINT_DIRECT_LOGIN(), { token });

      if (response && response.data?.[0]) {
        if (!localStorage.getItem('userLoggedIn')) {
          localStorage.setItem('userLoggedIn', 'true');
        }
        localStorage.setItem("userId", response.data[0].userId);
        if (response.data[0]?.server_url) {
          localStorage.setItem("serverUrl", response.data[0].server_url);
        }
        persistSessionTokens(response);
        startSessionRefreshScheduler();

        const sessionVariantId = getOrCreateSessionVariantId({ forceNew: true });
        persistSessionVariantId(sessionVariantId);
        window.location.reload();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Direct token login failed:', error);
      return false;
    }
  };

  const switchToParentAccount = async () => {
    const context = getChallengeSwitchContext();
    const parentUserDetailId = context?.parentUserDetailId;

    if (!parentUserDetailId) {
      return false;
    }

    try {
      const tokenResponse = await APIMiddleware.get(
        API_ENDPOINT_GENERATE_CHILD_LOGIN_TOKEN(parentUserDetailId, parentUserDetailId)
      );
      const token = tokenResponse?.data?.[0]?.directLoginToken;

      if (!token) {
        return false;
      }

      return loginWithDirectToken(token);
    } catch (error) {
      console.error('Switch to parent account failed:', error);
      return false;
    }
  };


  platformContextRef.current = {
    user, chartSubscription, loadPlatform, sendDataToServer, handleLiveFeedData, fetchUserLeverage, isLoggingOut,
  };

  return (
    <AuthContext.Provider
      value={{
        connectionClosed,
        allValidSymbols,
        user,
        login,
        logout,
        logoutWithOutRefresh,
        loginWithDirectToken,
        switchToParentAccount,
        isLoggingOut,
        updateUserData,
        updateUserData_Local,
        updateUserAuthorization,
        loadPlatform,
        platFromData: currentPlatformData,
        chartSubscription,
        leverageData,
        fetchUserLeverage,
        sendDataToServer,
        selectedAuthSymbol,
        setAuthSelectedSymbol,
        setAuthTimeFrame,
        selectedAuthTimeFrame,
        setAuthSelectedCategory,
        authSelectedCategory,
        fontAdjuster,
        fontSize,
        selectedAuthSymbolId,
        setSelectedAuthSymbolId,
        fontUpdating,
        setFontUpdating,
        fontSizeTrigger,
        defaultSelectedCategory,
        setShowLogoutModal,
        showLogoutModal,
        isMariginCallVisible, 
        setIsMariginCallVisible,
        isReconnected, 
        setIsReconnected,
        setAllValidSymbols,
        reloadApi, setReloadApi,
        logoutModal, setLogoutModal,
        loginPortalModal, setLoginPortalModal,isWebSocketRefetch,
        showFloatingWindow, setShowFloatingWindow,
        setVariantLogout , variantLogout,
        resetPasswordModal, showResetPasswordModal,
        isFirstLoginDisabled, setIsFirstLoginDisabled,
        incrementSystemNotificationCount,
        setSystemNotificationCount,
        setAlertNotificationCount
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
